// Firebase App Check for the customer app (reCAPTCHA Enterprise).
//
// Functions verify the token in `X-Firebase-AppCheck` next to the ID token
// (functions/security/appCheckGate.ts, `APP_CHECK_MODE`). The client side is
// strictly best effort: with no site key, a blocked reCAPTCHA script or a slow
// attestation, requests go out without the header and the server decides.
// Never fail a request here.
//
// Activation must happen right after initializeApp and before Firestore/Storage
// are created, so those SDKs pick up App Check for a later console enforcement.
const appCheckClientApi = (() => {
  const SITE_KEY_PATTERN = /^[A-Za-z0-9_-]{20,100}$/
  const DEBUG_TOKEN_STORAGE_KEY = 'plumme.appCheckDebugToken'
  const TOKEN_TIMEOUT_MS = 5000
  let instance: any = null

  // The build injects the key into index.html; an unreplaced placeholder or a
  // malformed value counts as "not configured".
  const siteKeyFrom = (raw: unknown) => {
    const value = String(raw ?? '').trim()
    return SITE_KEY_PATTERN.test(value) && !value.includes('__PLUMME_') ? value : ''
  }

  // Local development only: `localStorage['plumme.appCheckDebugToken'] = 'true'`
  // makes the SDK print a debug token to register in the console; a registered
  // UUID can be stored instead. Never honoured outside localhost.
  const debugTokenFor = (localhost: boolean): string | boolean => {
    if (!localhost) return ''
    try {
      const stored = String((globalThis as any).localStorage?.getItem?.(DEBUG_TOKEN_STORAGE_KEY) || '').trim()
      if (stored === 'true') return true
      return /^[0-9a-f-]{36}$/i.test(stored) ? stored : ''
    } catch {
      return ''
    }
  }

  const activate = (firebaseApp: any, { siteKey, localhost = false }: { siteKey?: unknown; localhost?: boolean } = {}) => {
    if (instance) return true
    const key = siteKeyFrom(siteKey)
    const sdk = (globalThis as any).firebase
    if (!key || !firebaseApp || typeof sdk?.appCheck !== 'function' || !sdk.appCheck.ReCaptchaEnterpriseProvider) return false
    try {
      const debugToken = debugTokenFor(localhost)
      if (debugToken) (globalThis as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken
      const appCheck = sdk.appCheck(firebaseApp)
      appCheck.activate(new sdk.appCheck.ReCaptchaEnterpriseProvider(key), true)
      instance = appCheck
      return true
    } catch (error) {
      console.warn('[plumme] App Check could not be activated; requests continue without it.', (error as any)?.code || '')
      return false
    }
  }

  const headers = async (): Promise<Record<string, string>> => {
    if (!instance) return {}
    try {
      let timer: any = null
      const timeout = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS) })
      const result: any = await Promise.race([instance.getToken(false), timeout]).finally(() => clearTimeout(timer))
      const token = typeof result?.token === 'string' ? result.token : ''
      return token ? { 'X-Firebase-AppCheck': token } : {}
    } catch {
      return {}
    }
  }

  return { activate, headers, siteKeyFrom, debugTokenFor, isActive: () => Boolean(instance) }
})()
Object.assign(globalThis, { PlummeAppCheck: appCheckClientApi })
